// src/pages/msme/MSMEThink5WhysMaturity.jsx
import React, { useState, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faChevronDown, 
  faChevronUp, 
  faDownload, 
  faCopy, 
  faStar 
} from "@fortawesome/free-solid-svg-icons";
import jsPDF from "jspdf";

const maturityStages = [
  {
    id: "stage-1",
    emoji: "🌱",
    level: "Start Right",
    state: "Awareness & Compliance",
    targetedOutcome: "Clarity & Readiness",
    keyModules: [
      "Awareness & Literacy",
      "Success Kit",
      "MSME Shelf",
      "MSME Monthly",
      "SAKSHAM Database",
      "Digital Card",
      "Consultants"
    ],
    color: "bg-emerald-700",
  },
  {
    id: "stage-2",
    emoji: "⚙️",
    level: "Work Smart",
    state: "Structured & Measured",
    targetedOutcome: "Efficiency & Control",
    keyModules: [
      "Goal Board",
      "Process & RACI Board",
      "Kanban",
      "LLM Capture",
      "MSME Year Book",
      "DeepThinking Lab",
      "Counselling",
      "Innovations"
    ],
    color: "bg-indigo-700",
  },
  {
    id: "stage-3",
    emoji: "💰",
    level: "Grow Wise",
    state: "Market Ready",
    targetedOutcome: "Scale & Profitability",
    keyModules: [
      "MSME Marketplace",
      "Digital Boutique",
      "CSAT",
      "BoloGrahak",
      "Forum",
      "TwiWorld",
      "NPA Solutions",
      "ValueShare",
      "Minutes of Meeting"
    ],
    color: "bg-amber-700",
  },
  {
    id: "stage-4",
    emoji: "🧠",
    level: "Lead Forward",
    state: "Innovation Driven",
    targetedOutcome: "Sustainability & Leadership",
    keyModules: [
      "BlueHands",
      "Design of Experiments",
      "Trainings & Workshops",
      "MSME Ranking",
      "MSME MetaVerse",
      "MSME Games",
      "My Advisor"
    ],
    color: "bg-violet-700",
  }
];

const cardHeaderClass =
  "flex items-center gap-3 p-3 rounded-lg text-sm font-semibold text-white";

const MSMEThink5WhysMaturity = () => {
  const [openStageId, setOpenStageId] = useState(null);
  const [copied, setCopied] = useState(false);
  const pdfRef = useRef();

  const toggleStage = (id) => {
    setOpenStageId(prev => (prev === id ? null : id));
  };

  const handleCopyStage = (stage) => {
    const text = `${stage.level} — ${stage.state}\nTargeted Outcome: ${stage.targetedOutcome}\nModules:\n- ${stage.keyModules.join("\n- ")}`;
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    }).catch(() => {
      // ignore
    });
  };

  const handleExportPDF = () => {
    const doc = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });
    doc.setFontSize(14);
    doc.text("SAKSHAM MSME — 4-Stage Maturity Model", 40, 40);
    let y = 70;
    maturityStages.forEach((s, idx) => {
      doc.setFontSize(12);
      doc.text(`${idx + 1}. ${s.level} ${s.emoji}`, 40, y);
      y += 18;
      doc.setFontSize(10);
      doc.text(`MSME State: ${s.state}`, 60, y);
      y += 14;
      doc.text(`Targeted Outcome: ${s.targetedOutcome}`, 60, y);
      y += 14;
      doc.text("Key Modules:", 60, y);
      y += 14;
      s.keyModules.forEach(mod => {
        if (y > 750) {
          doc.addPage();
          y = 40;
        }
        doc.text(`• ${mod}`, 80, y);
        y += 12;
      });
      y += 18;
    });
    doc.save("msme-maturity-model.pdf");
  };

  return (
    <div className="min-h-screen bg-gray-900 text-gray-100 p-4">
      {/* Header */}
      <header className="mb-6">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h1 className="text-lg text-start sm:text-xl font-bold">SAKSHAM — MSME 4-Stage Maturity Model</h1>
            <p className="text-sm text-gray-200 mt-1 max-w-xl">
              A progressive journey for millions of micro-enterprises and startups — focused on owner
              self-management, productivity and sustainable growth.
            </p>
          </div>
          <div className="flex gap-2 items-center">
            <button
              onClick={handleExportPDF}
              className="hidden sm:inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-800 border border-gray-700 hover:bg-gray-700 text-sm"
              title="Export full model as PDF"
            >
              <FontAwesomeIcon icon={faDownload} /> Export PDF
            </button>
          </div>
        </div>
      </header>

      <main ref={pdfRef} className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {maturityStages.map(stage => {
          const isOpen = openStageId === stage.id;
          return (
            <article key={stage.id} className="relative">
              <div className={`rounded-2xl overflow-hidden shadow-lg ${stage.color} bg-opacity-90`}>
                <div className={`${cardHeaderClass}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="text-2xl">{stage.emoji}</div>
                        <div>
                          <div className="text-sm font-bold leading-tight">{stage.level}</div>
                          <div className="text-xs text-gray-200/90 mt-0.5">{stage.state}</div>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <button
                          onClick={(e) => { e.stopPropagation(); toggleStage(stage.id); }}
                          aria-expanded={isOpen}
                          className="py-2 w-10 rounded-md bg-white bg-opacity-10 hover:bg-opacity-20"
                          title={isOpen ? "Collapse" : "Expand"}
                        >
                          <FontAwesomeIcon icon={isOpen ? faChevronUp : faChevronDown} />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="p-4 bg-gray-900/20">
                  <div className="text-xs text-gray-100 mb-3">
                    <span className="font-medium">Targeted Outcome:</span>{" "}
                    <span className="text-sm font-semibold">{stage.targetedOutcome}</span>
                  </div>

                  <div className={`transition-[max-height] duration-300 ease-in-out overflow-hidden ${isOpen ? "max-h-[600px]" : "max-h-[110px]"}`}>
                    <ul className="space-y-2 text-sm">
                      {stage.keyModules.map((m, idx) => (
                        <li key={m} className="flex items-start gap-3">
                          <span className="text-yellow-400 mt-0.5"><FontAwesomeIcon icon={faStar} /></span>
                          <div className="text-sm text-gray-100 leading-snug break-words">{m}</div>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </article>
          );
        })}
      </main>

      <div className="mt-6 text-center text-xs text-gray-200">
        Tap a card header to expand modules. Use PDF/Copy to export or share with stakeholders.
      </div>
    </div>
  );
};

export default MSMEThink5WhysMaturity;
